import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import img1banner from './../../../images/main-slider/1.jpeg';
import img2banner from './../../../images/main-slider/7.jpeg';
import img3banner from './../../../images/main-slider/14.jpeg';
import img4banner from './../../../images/main-slider/2.jpeg';
import bannerVideo from './../../../images/main-slider/bannervideo2.mp4';
import bannerVideo3 from './../../../images/main-slider/bannervideo3.mp4';
import bannerVideo4 from './../../../images/main-slider/bannervide4.mp4';

// Register GSAP plugin
if (typeof window !== 'undefined') {
	gsap.registerPlugin(ScrollTrigger);
}

const AwardSection = () => {
	const imageSectionRef = useRef(null);
	const textLine1Ref = useRef(null);
	const textLine2Ref = useRef(null);
	
	const imageSection2Ref = useRef(null);
	const textLine2_1Ref = useRef(null);
	const textLine2_2Ref = useRef(null);
	const textLine2_3Ref = useRef(null);
	
	const imageSection3Ref = useRef(null);
	const textLine3_1Ref = useRef(null);
	const textLine3_2Ref = useRef(null);
	
	const imageSection4Ref = useRef(null);
	const textLine4_1Ref = useRef(null);
	const textLine4_2Ref = useRef(null);
	const button4Ref = useRef(null);
	
	const imageSection5Ref = useRef(null);
	const textLine5_1Ref = useRef(null);
	const textLine5_2Ref = useRef(null);
	
	const imageSection6Ref = useRef(null);
	const textLine6_1Ref = useRef(null);
	const textLine6_2Ref = useRef(null);
	
	const imageSection7Ref = useRef(null);
	const textLine7_1Ref = useRef(null);
	const textLine7_2Ref = useRef(null);

	useEffect(() => {
		// Helper function to create animation
		const createAnimation = (triggerRef, textRefs, xOffset = 0) => {
			// Check if trigger element exists
			if (!triggerRef || !triggerRef.current) {
				console.warn('Trigger ref not found');
				return null;
			}

			// Filter valid text refs
			const validRefs = textRefs.filter(ref => ref && ref.current);
			if (validRefs.length === 0) {
				console.warn('No valid text refs found');
				return null;
			}

			const elements = validRefs.map(ref => ref.current);

			// Set initial state - ensure elements exist and are hidden
			elements.forEach(element => {
				if (element) {
					gsap.set(element, {
						opacity: 0,
						y: 100,
						x: xOffset,
						immediateRender: true,
						force3D: true,
						visibility: 'visible'
					});
				}
			});

			// Create timeline for smooth animation
			const tl = gsap.timeline({
				paused: true,
				scrollTrigger: {
					trigger: triggerRef.current,
					start: 'top 85%',
					end: 'top 15%',
					toggleActions: 'play none none reverse',
					once: false,
					invalidateOnRefresh: true,
					markers: false
				}
			});

			// Animate each text line
			elements.forEach((element, index) => {
				if (element) {
					tl.to(element, {
						duration: 1.2,
						opacity: 1,
						y: 0,
						x: 0,
						ease: 'power3.out',
						immediateRender: false,
						force3D: true,
						onComplete: () => {
							// Add class to ensure visibility
							if (element) {
								element.classList.add('animated');
								element.style.visibility = 'visible';
							}
						}
					}, index === 0 ? 0 : '-=0.9');
				}
			});

			// Ensure timeline is ready
			if (tl.duration() === 0) {
				console.warn('Timeline has no duration, adding fallback');
			}

			return tl;
		};

		// Wait for DOM to be fully ready and images loaded
		const timer = setTimeout(() => {
			// First image section animation
			if (imageSectionRef.current && textLine1Ref.current && textLine2Ref.current) {
				createAnimation(imageSectionRef, [textLine1Ref, textLine2Ref], -50);
			}

			// Second image section animation (centered)
			if (imageSection2Ref.current && textLine2_1Ref.current && textLine2_2Ref.current) {
				createAnimation(imageSection2Ref, [textLine2_1Ref, textLine2_2Ref], 0);
			}

			// Third image section animation (right side)
			if (imageSection3Ref.current && textLine3_1Ref.current && textLine3_2Ref.current) {
				createAnimation(imageSection3Ref, [textLine3_1Ref, textLine3_2Ref], 50);
			}

			// Fourth image section animation (centered with button)
			if (imageSection4Ref.current && textLine4_1Ref.current && textLine4_2Ref.current) {
				// Set initial state - text starts invisible (centered animation)
				gsap.set([textLine4_1Ref.current, textLine4_2Ref.current], {
					opacity: 0,
					y: 100,
					x: 0,
					immediateRender: true
				});

				// Set initial state for button
				if (button4Ref.current) {
					gsap.set(button4Ref.current, {
						opacity: 0,
						y: 50,
						scale: 0.9,
						immediateRender: true
					});
				}

				// Create timeline for smooth animation
				const tl4 = gsap.timeline({
					scrollTrigger: {
						trigger: imageSection4Ref.current,
						start: 'top 85%',
						end: 'top 15%',
						toggleActions: 'play none none reverse',
						once: false,
						invalidateOnRefresh: true
					}
				});

				// Animate first line
				tl4.to(textLine4_1Ref.current, {
					duration: 1.2,
					opacity: 1,
					y: 0,
					x: 0,
					ease: 'power3.out',
					immediateRender: false,
					force3D: true,
					onComplete: () => {
						textLine4_1Ref.current.classList.add('animated');
					}
				});

				// Animate second line (staggered)
				tl4.to(textLine4_2Ref.current, {
					duration: 1.2,
					opacity: 1,
					y: 0,
					x: 0,
					ease: 'power3.out',
					immediateRender: false,
					force3D: true,
					onComplete: () => {
						textLine4_2Ref.current.classList.add('animated');
					}
				}, '-=0.9');

				// Animate button
				if (button4Ref.current) {
					tl4.to(button4Ref.current, {
						duration: 1,
						opacity: 1,
						y: 0,
						scale: 1,
						ease: 'back.out(1.7)',
						immediateRender: false,
						force3D: true
					}, '-=0.5');
				}

			}

			// Fifth video section animation (centered)
			if (imageSection5Ref.current && textLine5_1Ref.current && textLine5_2Ref.current) {
				createAnimation(imageSection5Ref, [textLine5_1Ref, textLine5_2Ref], 0);
			}

			// Sixth video section animation (centered)
			if (imageSection6Ref.current && textLine6_1Ref.current && textLine6_2Ref.current) {
				createAnimation(imageSection6Ref, [textLine6_1Ref, textLine6_2Ref], 0);
			}

			// Seventh video section animation (centered)
			if (imageSection7Ref.current && textLine7_1Ref.current && textLine7_2Ref.current) {
				createAnimation(imageSection7Ref, [textLine7_1Ref, textLine7_2Ref], 0);
			}

			// Refresh ScrollTrigger after a short delay to ensure all elements are ready
			setTimeout(() => {
				ScrollTrigger.refresh();
				// Force update to ensure animations are registered
				ScrollTrigger.update();
				
				// Fallback: If elements are already in viewport, trigger animation manually
				const allSections = [
					{ ref: imageSectionRef, texts: [textLine1Ref, textLine2Ref] },
					{ ref: imageSection2Ref, texts: [textLine2_1Ref, textLine2_2Ref] },
					{ ref: imageSection3Ref, texts: [textLine3_1Ref, textLine3_2Ref] },
					{ ref: imageSection4Ref, texts: [textLine4_1Ref, textLine4_2Ref] },
					{ ref: imageSection5Ref, texts: [textLine5_1Ref, textLine5_2Ref] },
					{ ref: imageSection6Ref, texts: [textLine6_1Ref, textLine6_2Ref] },
					{ ref: imageSection7Ref, texts: [textLine7_1Ref, textLine7_2Ref] }
				];

				allSections.forEach(({ ref, texts }) => {
					if (ref.current) {
						const rect = ref.current.getBoundingClientRect();
						const isInViewport = rect.top < window.innerHeight * 0.85 && rect.bottom > 0;
						
						if (isInViewport) {
							texts.forEach((textRef, index) => {
								if (textRef.current) {
									const computedStyle = window.getComputedStyle(textRef.current);
									const opacity = parseFloat(computedStyle.opacity);
									
									if (opacity === 0 || isNaN(opacity)) {
										gsap.to(textRef.current, {
											duration: 0.8,
											opacity: 1,
											y: 0,
											x: 0,
											ease: 'power2.out',
											delay: index * 0.2
										});
										textRef.current.classList.add('animated');
									}
								}
							});
						}
					}
				});

				// Additional fallback: Show all text after 2 seconds if still hidden
				setTimeout(() => {
					allSections.forEach(({ ref, texts }) => {
						if (ref.current) {
							texts.forEach(textRef => {
								if (textRef.current) {
									const computedStyle = window.getComputedStyle(textRef.current);
									const opacity = parseFloat(computedStyle.opacity);
									
									if (opacity === 0 || isNaN(opacity)) {
										gsap.to(textRef.current, {
											duration: 0.6,
											opacity: 1,
											y: 0,
											x: 0,
											ease: 'power2.out'
										});
										textRef.current.classList.add('animated');
									}
								}
							});
						}
					});
				}, 2000);
			}, 200);
		}, 500);

		// Handle window resize to refresh ScrollTrigger
		const handleResize = () => {
			ScrollTrigger.refresh();
		};
		window.addEventListener('resize', handleResize);

		return () => {
			clearTimeout(timer);
			window.removeEventListener('resize', handleResize);
			// Cleanup all ScrollTriggers
			ScrollTrigger.getAll().forEach(trigger => {
				trigger.kill();
			});
		};
	}, []);

	return (
		<>
			<style>{`
				.award-section {
					padding: 80px 0px 0px 0px;
					background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
					position: relative;
					overflow: hidden;
				}
				.award-section::before {
					content: '';
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					bottom: 0;
					background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23e39001' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
					opacity: 0.5;
				}
				.award-container {
					position: relative;
					z-index: 1;
				}
				.award-header {
					text-align: center;
					margin-bottom: 60px;
				}
				.award-title {
					font-size: 42px;
					font-weight: 800;
					color: #1a1a1a;
					margin-bottom: 20px;
					line-height: 1.2;
					letter-spacing: -1px;
				}
				.award-title span {
					color:black;
				}
				.award-content {
					font-size: 1rem;
					line-height: 1.8;
					color: #555;
					max-width: 900px;
					margin: 0 auto;
				}
				.award-content strong {
					color: #1a1a1a;
					font-weight: 700;
				}
				
				/* Image Section Below */
				.award-image-section {
					position: relative;
					width: 100%;
					height: 100vh;
					min-height: 600px;
					overflow: hidden;
					display: flex;
					align-items: center;
					justify-content: flex-start;
					margin-top: 80px;
					background-size: cover;
					background-position: center;
					background-repeat: no-repeat;
					background-attachment: fixed;
				}
				.award-image-section-2 {
					position: relative;
					width: 100%;
					height: 100vh;
					min-height: 600px;
					overflow: hidden;
					display: flex;
					align-items: center;
					justify-content: center;
					margin-top: 0;
					background-size: cover;
					background-position: center;
					background-repeat: no-repeat;
					background-attachment: fixed;
				}
				.award-image-section-3 {
					position: relative;
					width: 100%;
					height: 100vh;
					min-height: 600px;
					overflow: hidden;
					display: flex;
					align-items: center;
					justify-content: flex-end;
					margin-top: 0;
					background-size: cover;
					background-position: center;
					background-repeat: no-repeat;
					background-attachment: fixed;
				}
				.award-image-section-4 {
					position: relative;
					width: 100%;
					height: 100vh;
					min-height: 600px;
					overflow: hidden;
					display: flex;
					align-items: center;
					justify-content: center;
					margin-top: 0;
					background-size: cover;
					background-position: center;
					background-repeat: no-repeat;
					background-attachment: fixed;
				}
				.award-image-overlay {
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					background: linear-gradient(135deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.15) 100%);
					z-index: 2;
				}
				.award-image-section .container-fluid {
					position: relative;
					z-index: 3;
					height: 100%;
					display: flex;
					align-items: center;
					justify-content: flex-start;
					padding: 0 80px;
				}
				.award-image-section-2 .container-fluid {
					position: relative;
					z-index: 3;
					height: 100%;
					display: flex;
					align-items: center;
					justify-content: center;
					padding: 0 80px;
				}
				.award-image-section-3 .container-fluid {
					position: relative;
					z-index: 3;
					height: 100%;
					display: flex;
					align-items: center;
					justify-content: flex-end;
					padding: 0 80px;
				}
				.award-image-section-4 .container-fluid {
					position: relative;
					z-index: 3;
					height: 100%;
					display: flex;
					align-items: center;
					justify-content: center;
					flex-direction: column;
					padding: 0 80px;
				}
				.award-image-text-wrapper {
					position: relative;
					z-index: 4;
					max-width: 60%;
					// background: rgba(0, 0, 0, 0.45);
					// backdrop-filter: blur(12px);
					// -webkit-backdrop-filter: blur(12px);
					// padding: 40px 50px;
					// border-radius: 20px;
					// border: 1px solid rgba(255, 255, 255, 0.1);
					// box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.4);
				}
				.award-image-section-2 .award-image-text-wrapper {
					text-align: center;
					max-width: 80%;
					padding: 45px 60px;
				}
				.award-image-section-3 .award-image-text-wrapper {
					text-align: right;
					max-width: 53%;
				}
				.award-image-section-4 .award-image-text-wrapper {
					text-align: center;
					// max-width: 80%;
					// margin-bottom: 40px;
					// padding: 45px 60px;
				}
				.award-image-text-line {
					font-size: 45px;
					font-weight: 700;
					color: #ffffff;
					line-height: 1.2;
					letter-spacing: 2px;
					text-transform: uppercase;
					font-family: OptimaLTPro Roman, sans-serif;
					text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
					margin-bottom: 12px;
					display: block;
					will-change: transform, opacity;
					backface-visibility: hidden;
					-webkit-font-smoothing: antialiased;
					-moz-osx-font-smoothing: grayscale;
					word-break: normal;
					white-space: normal;
					word-wrap: break-word;
					hyphens: auto;
					opacity: 0;
					transform: translateY(100px);
				}
				.award-image-text-line.animated {
					opacity: 1 !important;
					transform: translateY(0) !important;
				}
				.award-image-text-line:first-child {
					margin-bottom: 0;
				}
				.award-image-section-2 .award-image-text-line {
					text-align: center;
				}
				.award-image-section-3 .award-image-text-line {
					text-align: right;
				}
				.award-image-section-4 .award-image-text-line {
					text-align: center;
				}
				/* Glass Effect Button */
				.award-glass-button {
					position: relative;
					z-index: 4;
					padding: 18px 45px;
					font-size: 16px;
					font-weight: 600;
					text-transform: uppercase;
					letter-spacing: 2px;
					color: #fff;
					background: rgba(255, 255, 255, 0.1);
					backdrop-filter: blur(10px);
					-webkit-backdrop-filter: blur(10px);
					border: 1px solid rgba(255, 255, 255, 0.3);
					border-radius: 50px;
					text-decoration: none;
					display: inline-block;
					transition: all 0.3s ease;
					    box-shadow: 0 8px 32px 0 rgb(0 0 0 / 37%);
    overflow: hidden;
				}
				.award-glass-button::before {
					content: '';
					position: absolute;
					top: 0;
					left: -100%;
					width: 100%;
					height: 100%;
					background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
					transition: left 0.5s;
				}
				.award-glass-button:hover {
				color:black;
					background: rgba(255, 255, 255, 0.2);
					border-color: rgba(255, 255, 255, 0.5);
					transform: translateY(-2px);
					box-shadow: 0 12px 40px 0 rgba(31, 38, 135, 0.5);
				}
				.award-glass-button:hover::before {
					left: 100%;
				}
				.award-glass-button span {
					position: relative;
					z-index: 1;
				}
				/* Video Sections */
				.award-video-section {
					position: relative;
					width: 100%;
					height: 100vh;
					min-height: 600px;
					overflow: hidden;
					display: flex;
					align-items: center;
					justify-content: center;
					margin-top: 0;
				}
				.award-video-section video {
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					object-fit: cover;
					z-index: 0;
				}
				.award-video-section .container-fluid {
					position: relative;
					z-index: 3;
					height: 100%;
					display: flex;
					align-items: center;
					justify-content: center;
					flex-direction: column;
					padding: 0 80px;
				}
				.award-video-section .award-image-text-wrapper {
					text-align: center;
				}
				@media (max-width: 1199px) {
					.award-image-text-wrapper {
						padding: 35px 45px;
						max-width: 60%;
					}
					.award-image-section-2 .award-image-text-wrapper,
					.award-image-section-4 .award-image-text-wrapper {
						padding: 40px 50px;
					}
					.award-image-text-line {
						font-size: 56px;
					}
				}
				@media (max-width: 991px) {
					.award-image-section-3 .award-image-text-wrapper {
						text-align: right;
						max-width: 91%;
					}
					.award-section {
						padding: 60px 0;
					}
					.award-title {
						font-size: 32px;
					}
					.award-content {
						font-size: 16px;
					}
					.award-image-section,
					.award-image-section-2,
					.award-image-section-3,
					.award-image-section-4,
					.award-video-section {
						height: 80vh;
						min-height: 500px;
						margin-top: 60px;
					}
					.award-image-section-2,
					.award-image-section-3,
					.award-image-section-4,
					.award-video-section {
						margin-top: 0;
					}
					.award-image-overlay {
						background: linear-gradient(135deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.4) 100%);
					}
					.award-image-text-wrapper {
						padding: 30px 35px;
						max-width: 70%;
						// background: rgba(0, 0, 0, 0.55);
						// backdrop-filter: blur(10px);
						-webkit-backdrop-filter: blur(10px);
					}
					.award-image-section-2 .award-image-text-wrapper,
					.award-image-section-4 .award-image-text-wrapper {
						padding: 35px 40px;
					}
					.award-image-text-line {
						font-size: 36px;
						letter-spacing: 1px;
						line-height: 1.3;
					}
				}
				@media (max-width: 767px) {
					.award-image-text-line {
						font-size: 28px;
						letter-spacing: 0.8px;
						line-height: 1.35;
					}
					.award-image-text-wrapper {
						max-width: 85%;
						padding: 25px 25px;
					}
				}
				@media (max-width: 575px) {
					.award-image-section,
					.award-image-section-2,
					.award-image-section-3,
					.award-image-section-4,
					.award-video-section {
						height: 70vh;
						min-height: 400px;
						margin-top: 40px;
					}
					.award-image-section-2,
					.award-image-section-3,
					.award-image-section-4,
					.award-video-section {
						margin-top: 0;
					}
					.award-image-overlay {
						background: linear-gradient(135deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.4) 100%);
					}
					.award-glass-button {
						padding: 14px 35px;
						font-size: 10px;
					}
						.award-image-section-4 .award-image-text-wrapper{
						margin-bottom: 0px!important;}
					.award-image-text-wrapper {
						padding: 25px 20px;
						max-width: 90%;
						// background: rgba(0, 0, 0, 0.6);
						// backdrop-filter: blur(8px);
						// -webkit-backdrop-filter: blur(8px);
						border-radius: 15px;
					}
					.award-image-section-2 .award-image-text-wrapper,
					.award-image-section-4 .award-image-text-wrapper {
						padding: 20px 25px;
					}
					.award-image-text-line {
						font-size: 22px;
						letter-spacing: 0.5px;
						line-height: 1.5;
						word-break: normal;
						white-space: normal;
						word-wrap: break-word;
						hyphens: auto;
					}
				}
			`}</style>
			<section className="award-section" data-content="">
				<div className="container award-container">
					<div className="award-header">
						<h2 className="award-title wow fadeInUp" data-wow-duration="1s">
							<span>About Star Giga</span>
						</h2>
						<div className="award-content wow fadeInUp" data-wow-duration="1s" data-wow-delay="0.2s">
							<p>
								<strong>Star Giga Establishment Limited</strong> is a UAE-based community development company renowned for its development of landmark freehold properties in the UAE. Our project portfolio includes <strong>Goldcrest Views 1 and 2</strong>, <strong>Goldcrest Executive</strong> in Jumeira Lake Towers, Dubai, and <strong>Goldcrest Dreams Tower A and Tower B</strong> located in Emirates City, Ajman. We are committed to delivering premium residential and commercial developments that combine innovative design, superior quality, and exceptional value for our clients.
							</p>
						</div>
					</div>
				</div>
			</section>
			







			{/* Image Section with Text Overlay */}
			<section 
				className="award-image-section" 
				ref={imageSectionRef}
				style={{ backgroundImage: `url(${img1banner})` }}
			>
				<div className="award-image-overlay"></div>
				<div className="container-fluid">
					<div className="award-image-text-wrapper">
						<div className="award-image-text-line" ref={textLine1Ref}>
						Designed around comfort, 
						</div>
						<div className="award-image-text-line" ref={textLine2Ref}>
						peace, and long-term value.
						</div>
					</div>
				</div>
			</section>
			




			

			{/* Sixth Video Section with Text Overlay - No Gap */}
			<section 
				className="award-video-section" 
				ref={imageSection7Ref}
			>
				<video 
					autoPlay 
					loop 
					muted 
					playsInline
				>
					<source src={bannerVideo4} type="video/mp4" />
				</video>
				<div className="award-image-overlay"></div>
				<div className="container-fluid">
					<div className="award-image-text-wrapper">
						<div className="award-image-text-line" ref={textLine7_1Ref}>
						Thoughtfully planned
						</div>
						<div className="award-image-text-line" ref={textLine7_2Ref}>
						spaces for comfort living.
						</div>
					</div>
				</div>
			</section>




				{/* Second Image Section with Text Overlay - No Gap */}
				<section 
				className="award-image-section-2" 
				ref={imageSection2Ref}
				style={{ backgroundImage: `url(${img2banner})` }}
			>
				<div className="award-image-overlay"></div>
				<div className="container-fluid">
					<div className="award-image-text-wrapper">
						<div className="award-image-text-line" ref={textLine2_1Ref}>
						A strategic location
						</div>
						<div className="award-image-text-line" ref={textLine2_2Ref}>
						that keeps you connected.
						</div>
						
					</div>
				</div>
			</section>


	{/* Fifth Video Section with Text Overlay - No Gap */}
	<section 
				className="award-video-section" 
				ref={imageSection5Ref}
			>
				<video 
					autoPlay 
					loop 
					muted 
					playsInline
				>
					<source src={bannerVideo} type="video/mp4" />
				</video>
				<div className="award-image-overlay"></div>
				<div className="container-fluid">
					<div className="award-image-text-wrapper">
						<div className="award-image-text-line" ref={textLine5_1Ref}>
						A location that 
						</div>
						<div className="award-image-text-line" ref={textLine5_2Ref}>
						keeps you connected.
						</div>
					</div>
				</div>
			</section>




		
			
			{/* Third Image Section with Text Overlay on Right - No Gap */}
			{/* <section 
				className="award-image-section-3" 
				ref={imageSection3Ref}
				style={{ backgroundImage: `url(${img3banner})` }}
			>
				<div className="award-image-overlay"></div>
				<div className="container-fluid">
					<div className="award-image-text-wrapper">
						<div className="award-image-text-line" ref={textLine3_1Ref}>
							PREMIUM DEVELOPMENTS
						</div>
						<div className="award-image-text-line" ref={textLine3_2Ref}>
							EXCELLENCE DELIVERED
						</div>
					</div>
				</div>
			</section>
			 */}





<section 
				className="award-image-section-4" 
				ref={imageSection4Ref}
				style={{ backgroundImage: `url(${img4banner})` }}
			>
				<div className="award-image-overlay"></div>
				<div className="container-fluid">
					<div className="award-image-text-wrapper">
						<div className="award-image-text-line" ref={textLine4_1Ref}>
						A smart property investment
						</div>
						<div className="award-image-text-line" ref={textLine4_2Ref}>
						that grows with time.
						</div>
					</div>
					<Link to="/projects" className="award-glass-button" ref={button4Ref}>
						<span>Explore Projects</span>
					</Link>
				</div>
			</section>
			


				{/* Seventh Video Section with Text Overlay - No Gap */}
				<section 
				className="award-video-section" 
				ref={imageSection6Ref}
			>
				<video 
					autoPlay 
					loop 
					muted 
					playsInline
				>
					<source src={bannerVideo3} type="video/mp4" />
				</video>
				<div className="award-image-overlay"></div>
				<div className="container-fluid">
					<div className="award-image-text-wrapper">
						<div className="award-image-text-line" ref={textLine6_1Ref}>
						Home you can truly
						</div>
						<div className="award-image-text-line" ref={textLine6_2Ref}>
							call your own.
						</div>
					</div>
				</div>
			</section>


			
			{/* Fourth Image Section with Text and Glass Button - No Gap */}
		
		
			


		
		</>
	);
};

export default AwardSection;
